import React, { PropsWithChildren, useState } from 'react';

import ContributorCard from '@/components/shared/ContributorCard';
import {
	useDeleteContributorMutation,
	useDeleteVideoMutation,
	useListContributorsQuery,
	useListVideosQuery,
} from '@/graphql';
import { VideoInputSchema } from '@/graphql/validation';
import { useAuth } from '@/providers/AuthProvider';
import {
	Button,
	ButtonGroup,
	Flex,
	Popover,
	PopoverArrow,
	PopoverBody,
	PopoverCloseButton,
	PopoverContent,
	PopoverHeader,
	PopoverTrigger,
	Spinner,
	useDisclosure,
	useToast,
} from '@chakra-ui/react';

import CreateContributorModal, {
	CreateContributorInitialValues,
} from './components/CreateContributorModal';

const Contributors = () => {
	const { data, error, isLoading, refetch } = useListContributorsQuery();
	const { mutateAsync } = useDeleteContributorMutation({
		onSuccess: () => {
			refetch();
		},
	});
	const { isOpen, onOpen, onClose } = useDisclosure();
	const toast = useToast();
	const [initialData, setInitialData] =
		useState<CreateContributorInitialValues>();

	if (isLoading) {
		return <Spinner />;
	}

	if (error || !data) {
		throw error;
	}

	return (
		<>
			<CreateContributorModal
				isOpen={isOpen}
				onClose={onClose}
				onSuccess={refetch}
				initialData={initialData}
			/>
			<Flex gap="20px" mt="30px" alignItems="center" flexWrap="wrap">
				{data.listContributors.map((contributor) => {
					if (!contributor) {
						return null;
					}
					return (
						<ContributorCard
							flexShrink={0}
							key={contributor.id}
							w="300px"
							{...contributor}
							footer={
								<ButtonGroup spacing="2" w="100%">
									<Button
										variant="outline"
										onClick={() => {
											setInitialData(contributor);
											onOpen();
										}}
									>
										Edit
									</Button>
									<Popover>
										{({ onClose }) => (
											<>
												<PopoverTrigger>
													<Button
														variant="ghost"
														colorScheme="red"
													>
														Delete
													</Button>
												</PopoverTrigger>
												<PopoverContent>
													<PopoverArrow />
													<PopoverHeader>
														Confirmation!
													</PopoverHeader>
													<PopoverBody>
														<ButtonGroup
															spacing="2"
															w="100%"
														>
															<Button
																variant="solid"
																colorScheme="red"
																onClick={async () => {
																	try {
																		await mutateAsync(
																			{
																				contributorId:
																					contributor.id,
																			},
																		);

																		toast({
																			title: `Contributor ${contributor.name} deleted!`,
																		});
																		onClose();
																	} catch (err) {
																		console.error(
																			err,
																		);
																		toast({
																			title: 'Error deleting contributor!',
																			status: 'error',
																			description:
																				err instanceof
																				Error
																					? err.message
																					: 'Unkown error, check console',
																		});
																	}
																}}
															>
																Yes
															</Button>
															<Button
																variant="ghost"
																onClick={
																	onClose
																}
															>
																No
															</Button>
														</ButtonGroup>
													</PopoverBody>
												</PopoverContent>
											</>
										)}
									</Popover>
								</ButtonGroup>
							}
						/>
					);
				})}
			</Flex>

			<Button
				display="block"
				mx="auto"
				mt="150px"
				flexShrink={0}
				onClick={() => {
					setInitialData(undefined);
					onOpen();
				}}
			>
				Create contributor
			</Button>
		</>
	);
};

export default Contributors;
